import { forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { salesMessageCodeMap } from "modules/messageCodeSales"
import { salesDashboardMessageCodeMap } from "modules/messageCodeSalesDashboard"
import { useSite } from "modules/sites"

import Dialog from "components/Modality/Dialog"

const okayLabelMap = {
  error: "try_again",
  confirm: "confirm",
  success: "continue",
}

const Dialogue = ({ dialog, removeDialog }) => {
  const dialogRef = useRef()
  const modalRef = useRef()
  const { t } = useTranslation(["sales", "label"])
  const site = useSite()

  const messageCodeMap = (() => {
    switch (site.name) {
      case "sales":
      default:
        return salesMessageCodeMap
      case "dashboard":
        return salesDashboardMessageCodeMap
    }
  })()

  const handleClose = useCallback(() => {
    dialog.resolve()
    removeDialog(dialog)
  }, [dialog, removeDialog])

  // resolve with confirmed when onOK
  const handleOkay = useCallback(() => {
    dialog.resolve(true)
    modalRef.current.close()
  }, [dialog])

  const formatError = useCallback(
    (error) => {
      const getErrorMessage = (message, cause) => {
        const messageCode = messageCodeMap.get(parseInt(message, 10))
        return messageCode ? t(`message.${messageCode}`, cause) : message
      }

      const formatObjectError = (error) => {
        if (error?.message) {
          return getErrorMessage(error.message, error?.cause)
        }

        if (error.content instanceof Error) {
          return getErrorMessage(error.content.message, error.content?.cause)
        }

        return error.content
      }

      return typeof error === "object" ? formatObjectError(error) : error
    },
    [messageCodeMap, t],
  )

  useEffect(() => {
    if (modalRef.current) {
      const passDialog = dialog.options?.delegate
      if (typeof passDialog === "function") {
        passDialog(dialogRef.current)
      }
      modalRef.current?.open()
    }
  }, [dialog, formatError])

  useImperativeHandle(dialogRef, () => ({
    ...dialog,
    open: modalRef.current.open,
    close: modalRef.current.close,
  }))

  return (
    <Dialog
      ref={modalRef}
      variant={dialog.type !== "message" ? dialog.type : undefined}
      title={dialog.message?.name || dialog.message?.title || (dialog.type === "error" && t("label:error"))}
      caption={dialog.message?.title || (dialog.type === "error" && t("label:error"))}
      onClose={handleClose}
      withCancel={dialog.type === "confirm" || dialog.options?.withCancel}
      onOK={handleOkay}
      cancelLabel={dialog.message?.cancelLabel}
      okayLabel={
        dialog.message?.okayLabel || (okayLabelMap?.[dialog.type] ? t(`label:${okayLabelMap[dialog.type]}`) : null)
      }
      inert={dialog.options?.inert}>
      {dialog.type === "error"
        ? formatError(dialog.message)
        : typeof dialog.message === "object"
          ? dialog.message.content
          : dialog.message}
    </Dialog>
  )
}

const DialogOverlay = (props, ref) => {
  const [dialogs, setDialogs] = useState([])

  const removeDialog = useCallback(
    ({ token }) => setDialogs((state) => state.filter((dialog) => token !== dialog.token)),
    [],
  )

  useImperativeHandle(ref, () => ({
    open: (dialog) =>
      new Promise((resolve) => {
        const token = Symbol()
        setDialogs((state) =>
          state.concat({
            ...dialog,
            resolve,
            token,
            timeStamp: Date.now(), // React would not accept Symbol as keys
          }),
        )
      }),
    clear: (source) => {
      setDialogs((state) => state.filter((dialog) => source !== dialog.source))
    },
  }))

  return (
    // translation may trigger suspense
    <Suspense fallback={null}>
      {dialogs.map((dialog) => (
        <Dialogue key={dialog.timeStamp} dialog={dialog} removeDialog={removeDialog} />
      ))}
    </Suspense>
  )
}

export default forwardRef(DialogOverlay)
